import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import AccountMenu from './AccountMenu';
import { toast } from 'sonner';
import { Bell, Ticket } from 'lucide-react';
import api from '../utils/api';

const DashboardLayout = ({ children, setAuth }) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [ticketCount, setTicketCount] = useState(0);
  const [alertCount, setAlertCount] = useState(0);
  const previousTicketCountRef = useRef(null);
  const previousAlertCountRef = useRef(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const originalTitle = 'RMM Dashboard';
  const flashIntervalRef = useRef(null);
  const audioRef = useRef(null);
  const isInitialFetchRef = useRef(true);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    
    // Create audio element for notification sound
    try {
      audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSiGzvLaizsIHmy57OihUBALTKXh8LdoIAU7k9ryy3ksAxp0yO/dkUAKE1u16+qnVRQLSKDh8L5tIgUnh8zy2Yw/CA5ruu3pp1QRCk2l4fC4aB8FO5PZ8st5LAMZdMjv3pFAChNeturqqVUSCkuf4fG+bSEEJ4bM8dmMPwgPbLvt6adUEQpMpeDwt2cfBTuU2fLKeSkCGXTI796RQAoSXrbq6ahUEQlLn+Hxvm0hBCeFy/HZizwIC2y67OmnUxAKTKXh8LdnHgU7lNnyynkoAhh0yO/ekUAKE1626+moVBEJS5/i8b5tIQQnhcvx2Ys8CAxsvOzpp1MQCkyp4fC3Zx4EO5TZ8sp5KQIYdMjv3pJBChNdturqp1QQCUuf4fG+bCEFJ4bM8dmLPAgLbLvt6adUEApNqeHwt2YdBTuT2PK+aSkBGHPH796SPgoTXrXq6qhUEQlLn+Hxvm0hBCeFy/HZizsIC2y77OmmUxAJTKjg77dmHgU7k9jyynkpARh0x+/fkT4KE1216uqoVBEJS5/h8r5tIQQnhcvx2Ys7CAtru+3pp1MQCUyo4PC3Zh4EOpPY8sp5KQIXdMfv35E/ChNdterqqFQRCEuf4PK+bCEEJ4XL8diLOggLa7vs6adTEAlMqODvt2YeBTqT2PLJeSkBF3TH79+SPwoTXLXq6qhUEQhLn+Dyvmw=');
      audioRef.current.volume = 0.5; // Set volume to 50%
      console.log('✅ Audio element initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize audio element:', error);
    }
    
    // Fetch notification counts
    fetchNotificationCounts();
    
    // Refresh counts every 30 seconds
    const interval = setInterval(fetchNotificationCounts, 30000);
    
    return () => {
      clearInterval(interval);
      if (flashIntervalRef.current) {
        clearInterval(flashIntervalRef.current);
        document.title = originalTitle;
      }
    };
  }, []);
  
  const fetchNotificationCounts = async () => {
    try {
      // Fetch new tickets count (status: open, priority: high/critical, created in last 24h)
      const ticketsRes = await api.get('/tickets?status=open&limit=1000');
      const tickets = Array.isArray(ticketsRes.data) ? ticketsRes.data : ticketsRes.data.tickets || [];
      
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const newTickets = tickets.filter(t => {
        const createdAt = new Date(t.created_at);
        return createdAt > oneDayAgo && (t.priority === 'high' || t.priority === 'critical');
      });
      
      const newTicketCount = newTickets.length;
      const prevTicketCount = previousTicketCountRef.current;
      
      // Fetch active alerts count
      const alertsRes = await api.get('/alerts?status=active&limit=1000');
      const alerts = Array.isArray(alertsRes.data) ? alertsRes.data : alertsRes.data.alerts || [];
      const newAlertCount = alerts.length;
      const prevAlertCount = previousAlertCountRef.current;
      
      // Skip notification on initial fetch (only track counts)
      if (isInitialFetchRef.current) {
        console.log('Initial fetch - setting baseline counts:', { tickets: newTicketCount, alerts: newAlertCount });
        isInitialFetchRef.current = false;
      } else {
        // Check if counts increased (prevCount can be null or a number)
        const ticketsIncreased = prevTicketCount !== null && newTicketCount > prevTicketCount;
        const alertsIncreased = prevAlertCount !== null && newAlertCount > prevAlertCount;
        
        if (ticketsIncreased || alertsIncreased) {
          console.log('🔔 Notification triggered!', { 
            ticketsIncreased, 
            alertsIncreased,
            prevTickets: prevTicketCount,
            newTickets: newTicketCount,
            prevAlerts: prevAlertCount,
            newAlerts: newAlertCount
          });
          
          // Play audio alert
          if (audioRef.current) {
            console.log('Playing audio alert...');
            audioRef.current.play().catch(err => console.error('Audio play failed:', err));
          } else {
            console.error('Audio element not initialized!');
          }
          
          // Start flashing the browser tab
          startTabFlashing();
        }
      }
      
      // Update counts
      setTicketCount(newTicketCount);
      setAlertCount(newAlertCount);
      previousTicketCountRef.current = newTicketCount;
      previousAlertCountRef.current = newAlertCount;
      
    } catch (error) {
      // Fail silently - notifications are not critical
      console.error('Failed to fetch notification counts:', error);
    }
  };
  
  const startTabFlashing = () => {
    console.log('🔔 Starting tab flashing...');
    
    // Clear any existing flash interval
    if (flashIntervalRef.current) {
      clearInterval(flashIntervalRef.current);
      console.log('Cleared existing flash interval');
    }
    
    setIsFlashing(true);
    let isOriginal = true;
    
    // Flash for 30 seconds
    flashIntervalRef.current = setInterval(() => {
      document.title = isOriginal ? '🔔 NEW NOTIFICATION!' : originalTitle;
      isOriginal = !isOriginal;
      console.log('Tab title changed to:', document.title);
    }, 1000);
    
    console.log('Tab will flash for 30 seconds');
    
    // Stop flashing after 30 seconds
    setTimeout(() => {
      if (flashIntervalRef.current) {
        clearInterval(flashIntervalRef.current);
        flashIntervalRef.current = null;
      }
      document.title = originalTitle;
      setIsFlashing(false);
      console.log('✅ Tab flashing stopped');
    }, 30000);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuth(false);
    navigate('/login');
    toast.success('Logged out successfully');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Sidebar
        user={user}
        onLogout={handleLogout}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
      />

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
          collapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        {/* Top Bar with Account Menu */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
          <div className="flex items-center justify-between h-16 px-3 sm:px-6">
            <div className="flex-1" />
            
            {/* Notification Icons */}
            <div className="flex items-center gap-2 mr-4">
              {/* Tickets Notification */}
              <button
                onClick={() => navigate('/tickets')}
                className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors group"
                title="Open Tickets"
              >
                <Ticket className="w-5 h-5 text-slate-600 group-hover:text-blue-600 transition-colors" />
                {ticketCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-md animate-pulse">
                    {ticketCount > 99 ? '99+' : ticketCount}
                  </span>
                )}
              </button>
              
              {/* Alerts Notification */}
              <button
                onClick={() => navigate('/alerts')}
                className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors group"
                title="Active Alerts"
              >
                <Bell className="w-5 h-5 text-slate-600 group-hover:text-orange-600 transition-colors" />
                {alertCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-md animate-pulse">
                    {alertCount > 99 ? '99+' : alertCount}
                  </span>
                )}
              </button>
            </div>
            
            <AccountMenu user={user} onLogout={handleLogout} />
          </div>
        </div>

        {/* Page Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-2 sm:p-3 lg:p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
